import { type ChangeEvent, type FC, useMemo } from "react";
import { useEventCallback } from "usehooks-ts";
import type { UseFormReturn } from "../common/form/useForm";
import { ExtraForm } from "../common/pure/ExtraFormGroup";
import { useGoogleMyBusinessLocationAvailableAttributes } from "../settings/useIntegrations";
import styles from "./FormBusinessAttributes.module.css";
import type { FormMyBusinessLocationFields } from "./FormBusinessLocationFields";
import { groupAvailableAttributes } from "./helpers";

type Props = {
	form: UseFormReturn<FormMyBusinessLocationFields>;
	locationID: string;
};

export const FormBusinessAttributes: FC<Readonly<Props>> = ({
	form,
	locationID,
}) => {
	const availableAttributes =
		useGoogleMyBusinessLocationAvailableAttributes(locationID);

	const typedAvailableAttributes = useMemo(
		() => groupAvailableAttributes(availableAttributes, "valueType"),
		[availableAttributes],
	);

	const boolAttributeGroups = useMemo(
		() =>
			typedAvailableAttributes?.BOOL?.reduce<Array<string>>((acc, attr) => {
				if (acc.includes(attr.groupDisplayName)) {
					return acc;
				}

				acc.push(attr.groupDisplayName);
				return acc;
			}, []),
		[typedAvailableAttributes],
	);

	const urlAttributeGroups = useMemo(
		() =>
			typedAvailableAttributes?.URL?.reduce<Array<string>>((acc, attr) => {
				if (acc.includes(attr.groupDisplayName)) {
					return acc;
				}

				acc.push(attr.groupDisplayName);
				return acc;
			}, []),
		[typedAvailableAttributes],
	);

	const handleBoolAttributeChange = useEventCallback((id: string) => {
		const attributes = form.values.boolAttributes ?? [];
		const newAttributes = attributes.map((attr) => {
			if (attr.id === id) {
				return {
					...attr,
					value: !attr.value,
				};
			}
			return attr;
		});

		form.setFieldValue("boolAttributes", newAttributes);
	});

	const handleUrlAttributeChange = useEventCallback(
		(e: ChangeEvent<HTMLInputElement>, group: string) => {
			const attributes = form.values.urlAttributes ?? [];
			const attr = attributes.find((attr) => attr.id === group);
			if (attr) {
				attr.url = e.target.value;
			} else {
				attributes.push({ id: group, url: e.target.value });
			}
			form.setFieldValue("urlAttributes", attributes);
		},
	);

	return (
		<>
			{boolAttributeGroups?.map((group) => (
				<ExtraForm.Group className="form-checks" key={group} label={group}>
					<div className={styles.boolGroup}>
						{typedAvailableAttributes?.BOOL?.filter(
							(x) => x.groupDisplayName === group,
						).map((attr) => {
							const value =
								form.values?.boolAttributes?.find((x) => x.id === attr.parent)
									?.value ?? false;
							return (
								<ExtraForm.Checkbox
									key={attr.displayName}
									name={group}
									value={value}
									onChange={() => handleBoolAttributeChange(attr.parent)}
								>
									{attr.displayName}
								</ExtraForm.Checkbox>
							);
						})}
					</div>
				</ExtraForm.Group>
			))}
			{urlAttributeGroups?.map((group) => (
				<div key={group}>
					<h3>{group}</h3>
					{typedAvailableAttributes?.URL?.filter(
						(x) => x.groupDisplayName === group,
					).map((attr) => (
						<ExtraForm.Group key={attr.displayName} label={attr.displayName}>
							<ExtraForm.Control
								type="text"
								name={group}
								value={
									form?.values?.urlAttributes?.find((x) => x.id === attr.parent)
										?.url ?? ""
								}
								onChange={(e: ChangeEvent<HTMLInputElement>) =>
									handleUrlAttributeChange(e, attr.parent)
								}
							/>
						</ExtraForm.Group>
					))}
				</div>
			))}
		</>
	);
};
